import React, { useMemo } from 'react';
import { Modal, BarChart, PieChart, DataTable, AccessibleDataTableColumn } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { useLocalizedStrings } from '../../../../Localization';

type ChartDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
  component: SupportedDashboardComponent;
  queryResult?: TranslatedComponentResult;
  chartHeight?: number;
};

export const ChartDataModal: React.FC<ChartDataModalProps> = ({
  isOpen,
  onClose,
  component,
  queryResult,
  chartHeight = 400,
}) => {
  const L = useLocalizedStrings();

  const chartTitle = useMemo(() => {
    if ('title' in component && component.title) {
      return typeof component.title === 'string' ? component.title : component.title['en'];
    }
    return 'Chart Data';
  }, [component]);

  const chartElement = useMemo(() => {
    if (!queryResult) return null;

    if (component.kind === 'Bar Chart') {
      const groupKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const summaryKey = queryResult.summaryKey || '';
      
      return (
        <BarChart 
          showDataTable={false}
          data={queryResult.data}
          categoryKey={groupKey}
          valueKey={summaryKey}
          xAxisLabel={component.xAxisLabel}
          yAxisLabel={component.yAxisLabel}
          height={chartHeight}
          tableCaption={chartTitle}
        />
      );
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const dataKey = queryResult.summaryKey;
      
      let processedData = Array.isArray(queryResult.data) ? queryResult.data.map((datum: Record<string, any>) => ({
        ...datum,
        [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
      })) : [];

      // Handle data summarization for pie charts (same logic as in DashboardComponentCard)
      if (processedData.length > 5) {
        processedData.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0));
        const topFour = processedData.slice(0, 4);
        const remaining = processedData.slice(4);
        const otherValue = remaining.reduce((sum, item) => sum + (item[dataKey] || 0), 0);
        
        const otherSegment = {
          [nameKey]: L.admin_panel.dashboards.other,
          [dataKey]: otherValue
        };
        
        processedData = [...topFour, otherSegment];
      }

      return (
        <PieChart 
          showDataTable={false}
          height={chartHeight}
          data={processedData}
          dataKey={dataKey}
          nameKey={nameKey}
          tableCaption={chartTitle}
        />
      );
    }

    return null;
  }, [component, queryResult, chartHeight, chartTitle, L]);

  const tableColumns = useMemo((): AccessibleDataTableColumn[] => {
    if (!queryResult) return [];

    if (component.kind === 'Bar Chart') {
      const groupKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const summaryKey = queryResult.summaryKey || '';
      
      return [
        { 
          key: groupKey, 
          label: component.xAxisLabel || groupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        },
        { 
          key: summaryKey, 
          label: component.yAxisLabel || summaryKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    if (component.kind === 'Pie Chart') {
      const nameKey = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
      const dataKey = queryResult.summaryKey;
      
      return [
        { 
          key: nameKey, 
          label: nameKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        },
        { 
          key: dataKey, 
          label: dataKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          format: (value: number | string) => typeof value === 'number' ? value.toLocaleString() : value
        }
      ];
    }

    return [];
  }, [component, queryResult]);

  const tableData = useMemo(() => {
    if (!queryResult || !Array.isArray(queryResult.data)) return [];

    return queryResult.data;
  }, [queryResult]);

  if (!queryResult || (component.kind !== 'Bar Chart' && component.kind !== 'Pie Chart')) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => onClose()}
      title={chartTitle}
      size="fullscreen"
      className="max-w-7xl"
    >
      <div className="space-y-6">
        {/* Chart Section */}
        {chartElement}

        {/* Data Table Section */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="bg-white rounded border max-h-96 overflow-auto">
            <DataTable
              data={tableData}
              columns={tableColumns.map(col => ({
                header: col.label,
                accessorKey: col.key
              }))}
              pageSize={10}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};